import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { addIconGlassLg } from '@tpct/icons';

export type FileDropzoneProps = {
  accept: string;
  disabled?: boolean;
  uploading?: boolean;
  ariaLabel: string;
  primaryIdleText: string;
  primaryUploadingText: string;
  secondaryText?: string;
  error?: string;
  onFileSelected: (file: File) => void;
};

/** Same dropzone markup/classes as hub MediaUploader — hub file-upload styles apply. */
export const FileDropzone = ({
  accept,
  disabled = false,
  uploading = false,
  ariaLabel,
  primaryIdleText,
  primaryUploadingText,
  secondaryText,
  error,
  onFileSelected,
}: FileDropzoneProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const isDisabled = disabled || uploading;

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    onFileSelected(file);
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    handleFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setDragging(false);
    if (isDisabled) return;
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handleDragOver = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!isDisabled) setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const openPicker = () => {
    if (!isDisabled) fileInputRef.current?.click();
  };
  const primaryText = uploading ? primaryUploadingText : primaryIdleText;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="visually-hidden"
        onChange={handleFileSelect}
        disabled={isDisabled}
        aria-label={ariaLabel}
      />
      <button
        type="button"
        className={`dropzone media-uploader-dropzone${dragging ? ' dragging' : ''}${isDisabled ? ' disabled' : ''}`}
        onClick={() => { if (!isDisabled) openPicker(); }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        disabled={isDisabled}
        aria-busy={uploading}
      >
        <div className="dropzone-content">
          <span className="dropzone-icon">
            <img src={addIconGlassLg} alt="" aria-hidden="true" />
          </span>
          <span className="dropzone-text">
            <span className="dropzone-primary">{primaryText}</span>
            {!uploading && secondaryText ? (
              <span className="dropzone-secondary media-uploader-dropzone__hint">{secondaryText}</span>
            ) : null}
          </span>
        </div>
      </button>
      {error ? <p className="media-uploader__error">{error}</p> : null}
    </>
  );
};
